import { designTokens } from "@itandi/itandi-bb-ui";
import { Alertv2 } from "@itandi/itandi-bb-ui/Alertv2";
import { Buttonv2 } from "@itandi/itandi-bb-ui/Buttonv2";
import { DropZone } from "@itandi/itandi-bb-ui/DropZone";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import { Paragraph } from "@itandi/itandi-bb-ui/Paragraph";
import { useRouter } from "next/router";
import { useState } from "react";
import useSWRMutation from "swr/mutation";
import {
	DEAL_ESTIMATION_OCR_FILE_ACCEPT,
	dealEstimationOcrFileSchema
} from "~/models/DealEstimationOcrRequest";
import { PATH as OCR_REQUEST_PATH } from "~/pages/deals/[id]/estimations/ocr_requests/[requestId]";
import { isSuccess } from "~/utils/api/api_handler";
import { create } from "~/utils/api/deals/DealEstimationOcrRequest";
import { generatePath } from "~/utils/generatePath";
import { getZodSafeParseErrorMessage } from "~/utils/ZodUtil";

type Props = {
	dealId: number;
	handleClose: () => void;
};

export function DealEstimationOcrModal({ dealId, handleClose }: Props) {
	const router = useRouter();
	const { notify } = useNotification();
	const [file, setFile] = useState<File | null>(null);
	const error =
		file !== null
			? getZodSafeParseErrorMessage(dealEstimationOcrFileSchema.safeParse(file))
			: null;

	const { trigger, isMutating } = useSWRMutation(
		generatePath("/deals/:deal_id/deal_estimation_ocr_requests", {
			deal_id: dealId
		}),
		async () => await create(file!, dealId)
	);
	const handleSubmit = async (): Promise<void> => {
		if (file === null || error !== null || isMutating) return;
		const response = await trigger();
		if (isSuccess(response) && response.data !== null) {
			handleClose();
			void router.push(OCR_REQUEST_PATH(dealId, response.data.id));
		} else {
			notify({
				content: "エラーが発生しました。時間をおいて再度お試しください。",
				variant: "error"
			});
		}
	};

	return (
		<Modalv2 isOpen onClose={handleClose}>
			<ModalHeader title="AIによる見積書PDFの読み取り" />
			<ModalBody>
				<Paragraph mb={designTokens["spacing/m"]}>
					アップロードしたPDFをAIが読み取り、見積明細として追加します。
				</Paragraph>
				<DropZone
					accept={DEAL_ESTIMATION_OCR_FILE_ACCEPT}
					errors={error !== null ? [error] : undefined}
					help="PDFファイルを1件選択してください。"
					mb={designTokens["spacing/m"]}
					multiple={false}
					onDeleteFile={() => {
						setFile(null);
					}}
					onSelectFiles={(files) => {
						setFile(files.item(0));
					}}
					showFiles
				/>
				<Alertv2 variant="info">
					<Paragraph>既存の見積明細は上書きされません。</Paragraph>
				</Alertv2>
			</ModalBody>
			<ModalFooter
				leftArea={
					<Buttonv2
						disabled={isMutating}
						onClick={handleClose}
						variant="secondary"
					>
						キャンセル
					</Buttonv2>
				}
				rightArea={
					<Buttonv2
						disabled={file === null || error !== null || isMutating}
						loading={isMutating}
						onClick={handleSubmit}
						variant="primary"
					>
						読み取りを開始
					</Buttonv2>
				}
			/>
		</Modalv2>
	);
}
